export const MOYASAR_PROVIDER_METADATA_KEYS = [
  "transaction_id",
  "owner_type",
  "owner_id",
  "payment_type",
] as const;

export type MoyasarOwnerType = "organization" | "candidate";
export type MoyasarPaymentType =
  | "subscription"
  | "credit_pack"
  | "add_on"
  | "upgrade";

export interface MoyasarProviderBinding {
  transactionId: string;
  ownerType: MoyasarOwnerType;
  ownerId: string;
  paymentType: MoyasarPaymentType;
}

export interface MoyasarProviderMetadata {
  transaction_id: string;
  owner_type: MoyasarOwnerType;
  owner_id: string;
  payment_type: MoyasarPaymentType;
}

function requiredString(value: unknown, field: string): string {
  const normalized = typeof value === "string" ? value.trim() : "";
  if (!normalized) throw new Error(`MOYASAR_PROVIDER_BINDING_INVALID:${field}`);
  return normalized;
}

export function buildMoyasarProviderMetadata(
  binding: MoyasarProviderBinding,
): MoyasarProviderMetadata {
  const ownerType = requiredString(binding.ownerType, "owner_type");
  if (ownerType !== "organization" && ownerType !== "candidate") {
    throw new Error("MOYASAR_PROVIDER_BINDING_INVALID:owner_type");
  }
  const paymentType = requiredString(binding.paymentType, "payment_type");
  if (!["subscription", "credit_pack", "add_on", "upgrade"].includes(paymentType)) {
    throw new Error("MOYASAR_PROVIDER_BINDING_INVALID:payment_type");
  }

  return {
    transaction_id: requiredString(binding.transactionId, "transaction_id"),
    owner_type: ownerType,
    owner_id: requiredString(binding.ownerId, "owner_id"),
    payment_type: paymentType as MoyasarPaymentType,
  };
}

export function hasCompleteMoyasarProviderMetadata(
  value: unknown,
): value is MoyasarProviderMetadata {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const metadata = value as Record<string, unknown>;
  return MOYASAR_PROVIDER_METADATA_KEYS.every((key) =>
    typeof metadata[key] === "string" && metadata[key].trim().length > 0
  ) && (metadata.owner_type === "organization" || metadata.owner_type === "candidate") &&
    ["subscription", "credit_pack", "add_on", "upgrade"].includes(
      String(metadata.payment_type),
    );
}

export function moyasarProviderMetadataMatches(
  left: unknown,
  right: unknown,
): boolean {
  if (!hasCompleteMoyasarProviderMetadata(left) ||
    !hasCompleteMoyasarProviderMetadata(right)) return false;
  return MOYASAR_PROVIDER_METADATA_KEYS.every((key) => left[key] === right[key]);
}
